import { useState } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
  ArrowLeft,
  BadgeDollarSign,
  Calendar,
  Handshake,
  Pencil,
  Percent,
  Trash2,
} from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { formatCurrency } from '@/lib/format'
import {
  ConsignmentForm,
  type ConsignmentFormValues,
} from '@/features/consignment/consignment-form'
import {
  useConsignment,
  useDeleteConsignment,
  useRecordPayout,
  useRecordSale,
  useUpdateConsignment,
  useWithdrawConsignment,
} from '@/features/consignment/hooks'
import { requireFlow } from '@/features/auth/guards'
import {
  boatName,
  consignorName,
  CONSIGNMENT_STATUS_LABELS,
  CONSIGNMENT_STATUS_VARIANT,
} from '@/types/consignment'

export const Route = createFileRoute('/_authenticated/inventory/consignment/$consignmentId')({
  beforeLoad: requireFlow('inventory'),
  component: ConsignmentDetailPage,
})

const formatDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { dateStyle: 'medium' }) : '—'

function ConsignmentDetailPage() {
  const { consignmentId } = Route.useParams()
  const navigate = useNavigate()
  const { data: c, isLoading, isError } = useConsignment(consignmentId)

  const updateMutation = useUpdateConsignment()
  const deleteMutation = useDeleteConsignment()
  const saleMutation = useRecordSale()
  const payoutMutation = useRecordPayout()
  const withdrawMutation = useWithdrawConsignment()

  const [editOpen, setEditOpen] = useState(false)
  const [saleOpen, setSaleOpen] = useState(false)
  const [payoutOpen, setPayoutOpen] = useState(false)
  const [withdrawOpen, setWithdrawOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)

  const handleUpdateTerms = (values: ConsignmentFormValues) => {
    updateMutation.mutate(
      {
        id: consignmentId,
        body: {
          commission_rate: values.commission_rate,
          asking_price: values.asking_price,
          agreement_end: values.agreement_end || undefined,
          notes: values.notes || '',
        },
      },
      { onSuccess: () => setEditOpen(false) },
    )
  }

  const handleDelete = () => {
    deleteMutation.mutate(consignmentId, {
      // Land on the consignments list — history.back() can strand the user
      // (or exit the app) when the detail page was reached directly.
      onSuccess: () => navigate({ to: '/inventory', search: { tab: 'consignments' } }),
    })
  }

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[300px] w-full" />
      </div>
    )
  }

  if (isError || !c) {
    return <div className="text-destructive">Failed to load consignment.</div>
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <Link
            to="/inventory"
            search={{ tab: 'consignments' }}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" /> Inventory · Consignments
          </Link>
          <h1 className="flex items-center gap-2 text-3xl font-bold tracking-tight">
            <Handshake className="h-8 w-8" />
            {boatName(c)}
          </h1>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Badge variant="outline" className={CONSIGNMENT_STATUS_VARIANT[c.status]}>
              {CONSIGNMENT_STATUS_LABELS[c.status]}
            </Badge>
            <span>
              Consigned by{' '}
              {c.consignor ? (
                <Link
                  to="/sales/$customerId"
                  params={{ customerId: c.consignor_id }}
                  className="underline-offset-2 hover:underline"
                >
                  {consignorName(c)}
                </Link>
              ) : (
                '—'
              )}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {c.status === 'active' && (
            <>
              <Button onClick={() => setSaleOpen(true)}>Record Sale</Button>
              <Button variant="outline" onClick={() => setEditOpen(true)}>
                <Pencil className="mr-2 h-4 w-4" /> Edit Terms
              </Button>
              <Button variant="outline" onClick={() => setWithdrawOpen(true)}>
                Withdraw
              </Button>
            </>
          )}
          {c.status === 'sold' && (
            <Button onClick={() => setPayoutOpen(true)}>
              <BadgeDollarSign className="mr-2 h-4 w-4" /> Record Payout
            </Button>
          )}
          <Button variant="destructive" onClick={() => setDeleteOpen(true)}>
            <Trash2 className="mr-2 h-4 w-4" /> Delete
          </Button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Asking Price" value={formatCurrency(c.asking_price)} />
        <StatCard label="Commission" value={`${c.commission_rate}%`} icon={<Percent />} />
        <StatCard
          label="Sale Price"
          value={c.sale_price != null ? formatCurrency(c.sale_price) : '—'}
        />
        <StatCard
          label="Commission Amount"
          value={c.commission_amount != null ? formatCurrency(c.commission_amount) : '—'}
        />
        <StatCard
          label="Deductions"
          value={c.deductions != null ? formatCurrency(c.deductions) : '—'}
        />
        <StatCard
          label="Owner Proceeds"
          value={c.net_proceeds != null ? formatCurrency(c.net_proceeds) : '—'}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Calendar className="h-4 w-4" /> Agreement
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row label="Boat">
              <Link
                to="/inventory/$boatId"
                params={{ boatId: c.boat_id }}
                className="underline-offset-2 hover:underline"
              >
                {boatName(c)}
                {c.boat ? ` (${c.boat.year})` : ''}
              </Link>
            </Row>
            <Row label="Started">{formatDate(c.agreement_start)}</Row>
            <Row label="Ends">{formatDate(c.agreement_end)}</Row>
            <Row label="Sold">{formatDate(c.sold_at)}</Row>
            <Row label="Payout recorded">{formatDate(c.paid_out_at)}</Row>
            {c.notes && <Row label="Notes">{c.notes}</Row>}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Settlement</CardTitle>
            <CardDescription>
              Figures are snapshots computed when the sale was recorded.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row label="Sale price">
              {c.sale_price != null ? formatCurrency(c.sale_price) : 'Not yet sold'}
            </Row>
            <Row label={`Commission (${c.commission_rate}%)`}>
              {c.commission_amount != null ? formatCurrency(c.commission_amount) : '—'}
            </Row>
            <Row label="Deductions">
              {c.deductions != null ? formatCurrency(c.deductions) : '—'}
            </Row>
            <Row label="Net owner proceeds">
              {c.net_proceeds != null ? formatCurrency(c.net_proceeds) : '—'}
            </Row>
          </CardContent>
        </Card>
      </div>

      {/* Record Sale */}
      <RecordSaleDialog
        open={saleOpen}
        onOpenChange={setSaleOpen}
        consignmentId={c.id}
        defaultPrice={c.asking_price}
        pending={saleMutation.isPending}
        error={saleMutation.isError ? saleMutation.error.message : null}
        onSubmit={(body) =>
          saleMutation.mutate({ id: c.id, body }, { onSuccess: () => setSaleOpen(false) })
        }
      />

      {/* Record Payout */}
      <Dialog open={payoutOpen} onOpenChange={setPayoutOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Record payout</DialogTitle>
            <DialogDescription>
              Confirm that the net proceeds of{' '}
              <strong>{c.net_proceeds != null ? formatCurrency(c.net_proceeds) : '—'}</strong> were
              remitted to {consignorName(c)}.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayoutOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={payoutMutation.isPending}
              onClick={() =>
                payoutMutation.mutate({ id: c.id }, { onSuccess: () => setPayoutOpen(false) })
              }
            >
              Record Payout
            </Button>
          </DialogFooter>
          {payoutMutation.isError && (
            <p className="text-sm text-destructive">{payoutMutation.error.message}</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Withdraw */}
      <Dialog open={withdrawOpen} onOpenChange={setWithdrawOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Withdraw consignment</DialogTitle>
            <DialogDescription>
              End the agreement without a sale. The boat can be consigned again later; its listing
              state is left unchanged.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWithdrawOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={withdrawMutation.isPending}
              onClick={() =>
                withdrawMutation.mutate(c.id, { onSuccess: () => setWithdrawOpen(false) })
              }
            >
              Withdraw
            </Button>
          </DialogFooter>
          {withdrawMutation.isError && (
            <p className="text-sm text-destructive">{withdrawMutation.error.message}</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit terms */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Edit agreement terms</DialogTitle>
            <DialogDescription>
              Terms can only change while the agreement is active.
            </DialogDescription>
          </DialogHeader>
          <ConsignmentForm
            consignment={c}
            onSubmit={handleUpdateTerms}
            isPending={updateMutation.isPending}
            submitLabel="Save Terms"
          />
          {updateMutation.isError && (
            <p className="text-sm text-destructive">{updateMutation.error.message}</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete consignment</DialogTitle>
            <DialogDescription>
              Permanently remove this agreement record. This does not affect the boat.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={handleDelete}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function StatCard({
  label,
  value,
  icon,
}: {
  label: string
  value: string
  icon?: React.ReactNode
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{label}</CardDescription>
        <CardTitle className="flex items-center gap-2 text-2xl">
          {icon}
          {value}
        </CardTitle>
      </CardHeader>
      <CardContent />
    </Card>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right">{children}</span>
    </div>
  )
}

function RecordSaleDialog({
  open,
  onOpenChange,
  consignmentId,
  defaultPrice,
  pending,
  error,
  onSubmit,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  consignmentId: string
  defaultPrice: number
  pending: boolean
  error: string | null
  onSubmit: (body: { sale_price: number; sold_at?: string; deductions?: number }) => void
}) {
  const [salePrice, setSalePrice] = useState(String(defaultPrice))
  const [deductions, setDeductions] = useState('')
  const [soldAt, setSoldAt] = useState(() => new Date().toISOString().slice(0, 10))

  // Reset the mini-form each time the dialog opens for a fresh agreement.
  const [lastOpen, setLastOpen] = useState(false)
  if (open && !lastOpen) {
    setLastOpen(true)
    setSalePrice(String(defaultPrice))
    setDeductions('')
    setSoldAt(new Date().toISOString().slice(0, 10))
  } else if (!open && lastOpen) {
    setLastOpen(false)
  }

  const parse = (s: string) => Number(s.replace(/,/g, ''))
  const priceValid = !Number.isNaN(parse(salePrice)) && parse(salePrice) > 0
  const deductionsValid =
    deductions === '' || (!Number.isNaN(parse(deductions)) && parse(deductions) >= 0)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Record sale</DialogTitle>
          <DialogDescription>
            Commission and owner proceeds are computed from the agreement's rate and snapshotted —
            the boat is marked sold.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor={`sale-price-${consignmentId}`}>Sale price ($)</Label>
            <Input
              id={`sale-price-${consignmentId}`}
              inputMode="decimal"
              value={salePrice}
              onChange={(e) => setSalePrice(e.target.value.replace(/[^0-9.,]/g, ''))}
              placeholder="149,000"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`deductions-${consignmentId}`}>Deductions ($) — optional</Label>
            <Input
              id={`deductions-${consignmentId}`}
              inputMode="decimal"
              value={deductions}
              onChange={(e) => setDeductions(e.target.value.replace(/[^0-9.,]/g, ''))}
              placeholder="Repairs, detailing…"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`sold-at-${consignmentId}`}>Sale date</Label>
            <Input
              id={`sold-at-${consignmentId}`}
              type="date"
              value={soldAt}
              onChange={(e) => setSoldAt(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={pending || !priceValid || !deductionsValid}
            onClick={() =>
              onSubmit({
                sale_price: parse(salePrice),
                sold_at: soldAt || undefined,
                deductions: deductions === '' ? undefined : parse(deductions),
              })
            }
          >
            Record Sale
          </Button>
        </DialogFooter>
        {error && <p className="text-sm text-destructive">{error}</p>}
      </DialogContent>
    </Dialog>
  )
}
